import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { ExternalLink, FileText, Loader2 } from "lucide-react";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const toBase64Url = (value) => {
  const bytes = new TextEncoder().encode(value);
  let binary = "";

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
};

const buildSafeMediaUrl = (url) => {
  if (!url) return "";

  if (url.startsWith("/")) {
    return BACKEND_URL ? `${BACKEND_URL}${url}` : url;
  }

  const isTwilioUrl =
    /^https?:\/\//i.test(url) &&
    (url.includes("api.twilio.com") || url.includes("twilio.com/2010-04-01"));

  if (isTwilioUrl && BACKEND_URL) {
    return `${BACKEND_URL}/media/${toBase64Url(url)}`;
  }

  return url;
};

const openMedia = (url) => {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

const MediaItem = ({ media, sender = "patient" }) => {
  const [loaded, setLoaded] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  const type = media?.type || "";
  const isImage = type.startsWith("image/");
  const isVideo = type.startsWith("video/");
  const isAudio = type.startsWith("audio");
  const isDocument =
    type.startsWith("application/") || type === "application/octet-stream";
  const safeMediaUrl = buildSafeMediaUrl(media?.url);
  const displayUrl = blobUrl || safeMediaUrl;
  const documentLabel = type.includes("pdf") ? "Open PDF" : "Open document";

  const cardClasses =
    "rounded-2xl shadow-sm p-2 flex items-center justify-center overflow-hidden";

  // Fetch the media via backend and create a blob URL
  const getMediaUrl = useCallback(async () => {
    if (!media?.url) return null;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/media/post`,
        { url: media.url }, // send raw URL
        { responseType: "arraybuffer" }
      );

      const contentType =
        response.headers["content-type"] || "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error("Failed to fetch media:", err);
      return null;
    }
  }, [media?.url]);

  // Fetch blob URL on mount
  useEffect(() => {
    let cancelled = false;
    let activeBlobUrl = null;

    setLoaded(false);
    setBlobUrl(null);

    const setDoctorMediaUrl = () => {
      if (safeMediaUrl) setBlobUrl(safeMediaUrl);
    };

    const fetchRemoteMedia = async () => {
      if (isDocument) return;

      const nextBlobUrl = await getMediaUrl();

      if (cancelled) {
        if (nextBlobUrl) URL.revokeObjectURL(nextBlobUrl);
        return;
      }

      activeBlobUrl = nextBlobUrl;
      setBlobUrl(nextBlobUrl);
    };

    if (sender === "doctor") {
      setDoctorMediaUrl();
    } else {
      fetchRemoteMedia();
    }

    // Cleanup: revoke object URL when component unmounts
    return () => {
      cancelled = true;
      if (activeBlobUrl) URL.revokeObjectURL(activeBlobUrl);
    };
  }, [getMediaUrl, sender, safeMediaUrl, isDocument]);

  // Use blob URL if available, otherwise fallback to direct URL (shouldn't happen)
  
  return (
    <div className={`relative w-full max-w-[240px] ${cardClasses}`}>
      {/* Skeleton for images/videos */}
      {!loaded && (isImage || isVideo) && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-2xl" />
      )}

      {/* IMAGE */}
      {isImage && displayUrl && (
        <img
          src={displayUrl}
          alt="attachment"
          className={`rounded-2xl w-full max-h-[280px] object-contain cursor-pointer transition-opacity ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
          onLoad={() => setLoaded(true)}
          onClick={() => openMedia(displayUrl)}
        />
      )}

      {/* VIDEO */}
      {isVideo && displayUrl && (
        <video
          controls
          playsInline
          webkit-playsinline="true"
          preload="metadata"
          className={`rounded-2xl w-full max-h-[280px] min-h-[120px] bg-black transition-opacity ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
          onLoadedMetadata={() => setLoaded(true)}
        >
          <source src={displayUrl} type={type} />
          Your browser does not support video playback.
        </video>
      )}


      {/* AUDIO */}
      {isAudio && (
  <audio
    controls
    playsInline
    preload="metadata"
    src={
      sender === "doctor"
        ? `${process.env.REACT_APP_BACKEND_URL}/audio-play/${media.url.split("/").pop()}`
        : displayUrl
    }
  />
)}

      {/* DOCUMENT */}
      {isDocument && (
        <button
          type="button"
          onClick={() => openMedia(safeMediaUrl)}
          disabled={!safeMediaUrl}
          className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex min-w-0 items-center gap-2">
            <FileText size={18} className="shrink-0 text-slate-500" />
            <span className="truncate font-medium">{documentLabel}</span>
          </span>
          {safeMediaUrl ? (
            <ExternalLink size={16} className="shrink-0 text-slate-400" />
          ) : (
            <Loader2 size={16} className="shrink-0 animate-spin text-slate-400" />
          )}
        </button>
      )}
    </div>
  );
};

export default MediaItem;
